// Centralized NFP API client (uses your existing apiFetch helper)
import { apiFetch, setAuthToken } from '../api/client';

const DEFAULT_PAYOUT_SETTINGS = {
  cadence: 'monthly',
  depositDay: 15,
  minDisbursementCents: 5000,
};

const EMPTY_ASSETS = Object.freeze({ logoUrl: '', images: [] });
const IS_DEV = process.env.NODE_ENV !== 'production';
const q = (obj = {}) =>
  Object.entries(obj)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

let activeCauseContext = null;
let cachedDashboard = null;

const toIdString = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') return value.toString();
  return null;
};

function rememberCauseContext(cause) {
  if (!cause) return;
  activeCauseContext = {
    objectId: toIdString(cause._id) || activeCauseContext?.objectId || null,
    causeId: cause.causeId || activeCauseContext?.causeId || null,
  };
}

function ensureCauseContext() {
  if (activeCauseContext?.objectId || activeCauseContext?.causeId) {
    return activeCauseContext;
  }
  throw new Error('No cause context available. Load the dashboard first.');
}

function normalizeCauseOption(option) {
  if (!option) return undefined;
  if (typeof option === 'string') return option;
  if (typeof option === 'object') {
    return option.causeId || option.id || option._id || undefined;
  }
  return undefined;
}

function formatUploadLimitLabel(bytes) {
  const mb = Number(bytes) / (1024 * 1024);
  return Number.isInteger(mb) ? `${mb} MB` : `${mb.toFixed(1)} MB`;
}

function buildFriendlyUploadError(message, { kind = 'generic' } = {}) {
  const raw = String(message || '').trim();
  const label = kind === 'logo' || kind === 'brand-logo'
    ? 'logo'
    : kind === 'image' || kind === 'avatar' || kind === 'post-media'
      ? 'image'
      : 'file';
  const selected = label === 'logo' ? 'selected logo' : label === 'image' ? 'selected image' : 'selected file';
  const alternate = label === 'file' ? 'different file' : 'different image';

  const match = raw.match(/File exceeds\s+(\d+)\s*MB limit\.?/i);
  if (match) {
    const limitLabel = `${match[1]} MB`;
    if (label === 'logo' || label === 'image') {
      return `Image too large. Please choose a file that is ${limitLabel} or smaller.`;
    }
    return `File too large. Please choose a file that is ${limitLabel} or smaller.`;
  }
  if (/unsupported content type/i.test(raw)) {
    if (label === 'logo') return 'Please upload a logo image file, such as a JPG, PNG, or WebP.';
    if (label === 'image') return 'Please upload an image file, such as a JPG, PNG, or WebP.';
    return 'Please upload a supported file type.';
  }
  if (/invalid file size|missing file|empty/i.test(raw)) {
    return label === 'file'
      ? "We couldn't read that file. Please try a different file."
      : "We couldn't read that image file. Please try a different image.";
  }
  if (/service temporarily unavailable|s3 upload provider is not enabled|s3 upload bucket is not configured/i.test(raw)) {
    return label === 'logo'
      ? 'Logo upload is temporarily unavailable. Please try again in a minute.'
      : label === 'image'
        ? 'Image upload is temporarily unavailable. Please try again in a minute.'
        : 'File upload is temporarily unavailable. Please try again in a minute.';
  }
  return `We couldn't upload the ${selected}. Please try again or choose a ${alternate}.`;
}

function getUploadMaxBytes(kind) {
  if (kind === 'logo' || kind === 'image' || kind === 'proof' || kind === 'generic') {
    return 5 * 1024 * 1024;
  }
  return null;
}

function dashboardDefaults() {
  return {
    cause: null,
    stripeStatus: 'not_connected',
    payoutSettings: DEFAULT_PAYOUT_SETTINGS,
    profileChecklist: {},
    assets: EMPTY_ASSETS,
  };
}

export const nfpApi = {
  async startPendingRegistration({ fullName, email, phone, password, role = 'admin' }) {
    const [firstName, ...rest] = String(fullName || '').trim().split(' ');
    const lastName = rest.join(' ') || '';
    const resp = await apiFetch('/api/auth/register-pending', {
      method: 'POST',
      body: {
        firstName,
        lastName,
        email: String(email || '').trim(),
        phone: String(phone || '').trim(),
        password: String(password || ''),
        role,
      },
    });

    if (resp?.token) {
      setAuthToken(resp.token);
    }
    if (typeof window !== 'undefined' && resp?.user) {
      try {
        localStorage.setItem('user', JSON.stringify(resp.user));
      } catch {
        // ignore storage issues
      }
    }
    return resp;
  },

  /* ------------------ REGISTRATION ------------------ */
  startRegistration() {
    return apiFetch('/api/cause-registrations/start', { method: 'POST', body: {} });
  },
  checkEmailExists(email) {
    return apiFetch(`/api/auth/exists?${q({ email })}`);
  },
  checkOrgDuplicate(payload) {
    return apiFetch('/api/causes/dupe-check', { method: 'POST', body: payload });
  },
  putApplicant(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/applicant`, { method: 'PUT', body: payload });
  },
  listMyRegistrations() {
    return apiFetch('/api/cause-registrations/mine');
  },
  updateRegistrationMeta(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/meta`, { method: 'PATCH', body: payload });
  },
  putEntity(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/entity`, { method: 'PUT', body: payload });
  },
  putProof(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/proof`, { method: 'PUT', body: payload });
  },
  updateProof(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/proof`, { method: 'PUT', body: payload });
  },
  putBranding(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/branding`, { method: 'PUT', body: payload });
  },
  putSupporterSnapshot(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/supporter-snapshot`, { method: 'PUT', body: payload });
  },
  updateLocations(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/locations`, { method: 'PUT', body: payload });
  },
  putLocations(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/locations`, { method: 'PUT', body: payload });
  },
  updateAgreements(regId, payload) {
    return apiFetch(`/api/cause-registrations/${regId}/agreements`, { method: 'PUT', body: payload });
  },
  async uploadAsset(file, { kind = 'generic', folder } = {}) {
    const filename = file?.name || 'upload.bin';
    const contentType = file?.type || 'application/octet-stream';
    const size = Number(file?.size || 0);
    const maxBytes = getUploadMaxBytes(kind);
    if (maxBytes && size > maxBytes) {
      const prefix = kind === 'logo' || kind === 'image' ? 'Image' : 'File';
      throw new Error(`${prefix} too large. Please choose a file that is ${formatUploadLimitLabel(maxBytes)} or smaller.`);
    }
    let presign;
    try {
      presign = await apiFetch('/api/uploads/presign', {
        method: 'POST',
        body: {
          kind,
          folder,
          filename,
          contentType,
          size,
        },
      });
    } catch (err) {
      if (err?.status === 503 && IS_DEV) {
        const form = new FormData();
        form.append('file', file);
        form.append('kind', kind);
        if (folder) form.append('folder', folder);
        return apiFetch('/api/uploads', { method: 'POST', body: form });
      }
      if (err?.status === 503) {
        throw new Error(buildFriendlyUploadError(err?.data?.error || err?.message, { kind }));
      }
      throw new Error(buildFriendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    if (!presign?.uploadUrl) {
      throw new Error(buildFriendlyUploadError('Missing upload URL from server', { kind }));
    }

    let putRes;
    try {
      putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          ...(presign.headers || {}),
          'Content-Type': contentType,
        },
        body: file,
      });
    } catch (err) {
      throw new Error(buildFriendlyUploadError(err?.message, { kind }));
    }

    if (!putRes.ok) {
      const raw = await putRes.text().catch(() => '');
      throw new Error(buildFriendlyUploadError(raw || 'Direct upload failed', { kind }));
    }

    let complete;
    try {
      complete = await apiFetch('/api/uploads/complete', {
        method: 'POST',
        body: {
          key: presign.key,
          fileUrl: presign.fileUrl,
          kind,
        },
      });
    } catch (err) {
      throw new Error(buildFriendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    return {
      ...complete,
      key: presign.key,
      url: presign.fileUrl,
      location: presign.fileUrl,
      name: filename,
      mime: contentType,
      size,
      id: presign.key,
      _id: presign.key,
    };
  },
  submitRegistration(regId) {
    return apiFetch(`/api/cause-registrations/${regId}/submit`, { method: 'POST', body: {} });
  },

  async submitFinalOnboarding(regId) {
    if (!regId) throw new Error('Registration ID is required.');

    const submitResp = await apiFetch('/api/cause-registrations/' + regId + '/submit', {
      method: 'POST',
      body: {},
    });

    const completeResp = await apiFetch('/api/nfp/registration/' + regId + '/complete', {
      method: 'POST',
      body: {},
    });

    if (completeResp?.causeId) {
      activeCauseContext = {
        causeId: completeResp.causeId,
        objectId: activeCauseContext?.objectId || null,
      };
    }

    return { ...submitResp, ...completeResp };
  },

  /* ------------------ DASHBOARD ------------------ */
  async getDashboard(options) {
    const causeId = normalizeCauseOption(options);
    const qs = causeId ? `?causeId=${encodeURIComponent(causeId)}` : '';
    const resp = await apiFetch(`/api/nfp/dashboard${qs}`);
    if (resp?.cause) rememberCauseContext(resp.cause);
    cachedDashboard = {
      ...dashboardDefaults(),
      ...resp,
      payoutSettings: resp?.payoutSettings || DEFAULT_PAYOUT_SETTINGS,
      stripeStatus: resp?.stripeStatus || 'not_connected',
      assets: resp?.assets || EMPTY_ASSETS,
    };
    return cachedDashboard;
  },

  async getVerification(options) {
    const optionId = normalizeCauseOption(options);
    const ctx = optionId ? null : activeCauseContext;
    const causeId = optionId || ctx?.causeId || ctx?.objectId;
    const qs = causeId ? `?causeId=${encodeURIComponent(causeId)}` : '';
    const resp = await apiFetch(`/api/nfp/verification${qs}`);
    if (resp?.causeId || resp?.causeMongoId) {
      activeCauseContext = {
        causeId: resp.causeId || activeCauseContext?.causeId || null,
        objectId: resp.causeMongoId || activeCauseContext?.objectId || null,
      };
    }
    return resp;
  },

  async saveVerificationProof(payload = {}, options) {
    const optionId = normalizeCauseOption(options);
    const ctx = optionId ? null : activeCauseContext;
    const causeId = optionId || ctx?.causeId || ctx?.objectId;
    return apiFetch('/api/nfp/verification/proof', {
      method: 'PUT',
      body: { ...payload, ...(causeId ? { causeId } : {}) },
    });
  },

  async submitVerification(options) {
    const optionId = normalizeCauseOption(options);
    const ctx = optionId ? null : activeCauseContext;
    const causeId = optionId || ctx?.causeId || ctx?.objectId;
    return apiFetch('/api/nfp/verification/submit', {
      method: 'POST',
      body: causeId ? { causeId } : {},
    });
  },

  async getChecklist(options) {
    const causeId = normalizeCauseOption(options);
    const qs = causeId ? `?causeId=${encodeURIComponent(causeId)}` : '';
    const resp = await apiFetch(`/api/nfp/checklist${qs}`);
    if (resp?.causeMongoId || resp?.causeId) {
      activeCauseContext = {
        objectId: resp.causeMongoId || activeCauseContext?.objectId || null,
        causeId: resp.causeId || activeCauseContext?.causeId || null,
      };
    }
    if (cachedDashboard && resp?.profileChecklist) {
      cachedDashboard = { ...cachedDashboard, profileChecklist: resp.profileChecklist };
    }
    return resp;
  },

  async beginStripeConnect() {
    const ctx = ensureCauseContext();
    try {
      return await apiFetch('/api/nfp/stripe/connect', {
        method: 'POST',
        body: { causeId: ctx.causeId || ctx.objectId },
      });
    } catch (err) {
      if (err?.status === 501) {
        return { url: null, error: err.message || 'Stripe onboarding not enabled yet.' };
      }
      throw err;
    }
  },

  async getStripeStatus(options) {
    const optionId = normalizeCauseOption(options);
    const ctx = optionId ? null : ensureCauseContext();
    const causeId = optionId || ctx?.causeId || ctx?.objectId;
    const qs = causeId ? `?causeId=${encodeURIComponent(causeId)}` : '';
    try {
      return await apiFetch(`/api/nfp/stripe/status${qs}`);
    } catch (err) {
      if (err?.status === 404) {
        return { stripeStatus: cachedDashboard?.stripeStatus || 'not_connected' };
      }
      throw err;
    }
  },

  async refreshStripeStatus(options) {
    const optionId = normalizeCauseOption(options);
    const ctx = optionId ? null : ensureCauseContext();
    const causeId = optionId || ctx?.causeId || ctx?.objectId;
    const body = causeId ? { causeId } : {};
    return apiFetch('/api/nfp/stripe/status/refresh', {
      method: 'POST',
      body,
    });
  },

  async savePayoutSettings(payoutSettings) {
    const ctx = ensureCauseContext();
    const target = ctx.causeId || ctx.objectId;
    const resp = await apiFetch(`/api/causes/settings/${target}`, {
      method: 'PATCH',
      body: { payoutSettings },
    });
    if (resp?.payoutSettings && cachedDashboard) {
      cachedDashboard = { ...cachedDashboard, payoutSettings: resp.payoutSettings };
    }
    return resp;
  },

  async inviteTeammate(payload = {}) {
    const ctx = ensureCauseContext();
    try {
      return await apiFetch('/api/nfp/memberships/invite', {
        method: 'POST',
        body: { ...payload, causeId: ctx.causeId || ctx.objectId },
      });
    } catch (err) {
      if (err?.status === 501) {
        return { ok: false, error: err.message || 'Invites are not enabled yet.' };
      }
      throw err;
    }
  },

  async saveWhySupport(input) {
    const ctx = ensureCauseContext();
    const target = ctx.causeId || ctx.objectId;
    const text = typeof input === 'string' ? input : input?.text || '';
    const resp = await apiFetch(`/api/nfp/cause/${target}/why-support`, {
      method: 'PUT',
      body: { text },
    });
    if (resp?.cause) {
      rememberCauseContext(resp.cause);
      cachedDashboard = cachedDashboard
        ? {
            ...cachedDashboard,
            cause: resp.cause,
            profileChecklist: resp.profileChecklist || cachedDashboard.profileChecklist,
          }
        : {
            ...dashboardDefaults(),
            cause: resp.cause,
            profileChecklist: resp.profileChecklist || {},
          };
    }
    return resp;
  },

  async saveImpactMilestones(list) {
    const ctx = ensureCauseContext();
    const target = ctx.causeId || ctx.objectId;
    const milestones = Array.isArray(list) ? list : [];
    const resp = await apiFetch(`/api/nfp/cause/${target}/impact-milestones`, {
      method: 'PUT',
      body: { milestones },
    });
    if (resp?.cause) {
      rememberCauseContext(resp.cause);
      cachedDashboard = cachedDashboard
        ? {
            ...cachedDashboard,
            cause: resp.cause,
            profileChecklist: resp.profileChecklist || cachedDashboard.profileChecklist,
          }
        : {
            ...dashboardDefaults(),
            ...resp,
          };
    }
    return resp;
  },

  async saveAssets(payload = {}) {
    const ctx = ensureCauseContext();
    const target = ctx.causeId || ctx.objectId;
    // Normalize images to only send what backend cares about
    const images = Array.isArray(payload.images)
      ? payload.images.map((img) => ({
          url: img.url,
          id: img.id || img._id,
          focalPoint: img.focalPoint,
          scale: img.scale,
          heroMode: img.heroMode,
          isHero: !!img.isHero,
        }))
      : undefined;

    const resp = await apiFetch(`/api/nfp/cause/${target}/assets`, {
      method: 'PUT',
      body: { ...payload, ...(images ? { images } : {}) },
    });
    if (resp?.cause) {
      rememberCauseContext(resp.cause);
      cachedDashboard = cachedDashboard
        ? {
            ...cachedDashboard,
            cause: resp.cause,
            assets: resp.assets,
            profileChecklist: resp.profileChecklist || cachedDashboard.profileChecklist,
            visibility: resp.visibility || cachedDashboard.visibility,
            payoutReadiness: resp.payoutReadiness || cachedDashboard.payoutReadiness,
          }
        : { ...dashboardDefaults(), ...resp };
    }
    return resp;
  },

  async setHeadquarters(locationId) {
    const ctx = ensureCauseContext();
    const target = ctx.causeId || ctx.objectId;
    return apiFetch(`/api/nfp/locations/${encodeURIComponent(locationId)}/hq`, {
      method: 'POST',
      body: { causeId: target },
    });
  },

  /* ------------------ PORTAL LISTS ------------------ */
  getMine() {
    return apiFetch('/api/nfp/mine');
  },
  getMineSummary(params) {
    const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
    return apiFetch(`/api/nfp/mine/summary${qs}`);
  },
  getDisbursements(causeId) {
    return apiFetch(`/api/nfp/disbursements?causeId=${causeId}`);
  },

  /* ------------------ SETTINGS (secure) ------------------ */
  getCause(causeId) {
    return apiFetch(`/api/causes/${causeId}`);
  },
  updateProfile(causeId, payload) {
    return apiFetch(`/api/causes/${causeId}/profile`, { method: 'PUT', body: payload });
  },
  updatePayoutSettings(causeId, payload) {
    return apiFetch(`/api/causes/settings/${causeId}`, {
      method: 'PATCH',
      body: { payoutSettings: payload },
    });
  },
};
